import { Box, Button, Theme, Typography } from "@material-ui/core";
import React, { FC, useEffect } from "react";
import {
  ExamSessionForSignupViewModel,
  SignupExamSessionExamDaysModel,
} from "../../model/ExamSignup/ExamSessionForSignupViewModel";
import { makeStyles } from "@material-ui/core";
import { useIntl } from "react-intl";
import { Language, SwissGermanLocale, Switzerland, UrlQueryParameters } from "../../data/constants";
import { GetTranslatedValueIntl } from "../../model/Shared/TranslatedValue";
import Alert from "@material-ui/lab/Alert";
import { getKeyFromLanguage } from "../../utils/LanguageHelper";
import { navigateTo } from "../../utils/Navigation";
import { routesPaths } from "../../Routes";
import { useHistory } from "react-router-dom";
import { useGetLifeExamSessionDaysAvailableForSignUpQuery } from "../../api/rtk/SignUpApi";
import LoadingIndicator from "../common/LoadingIndicator";
import { getDateFromString } from "../../utils/Dates";
import TranslatedMessage from "../translations/TranslatedMessage";
import { useSnackbar } from "notistack";
import { initModulePayment } from "../../api/CartApi";
import { PaymentMethodType } from "../payment/PaymentConstants";
import { ModuleSelectionExamDetails } from "../../model/ExamSignup/ModuleSelectionViewModel";
import { store } from "../..";
import { LoadingSpinner } from "../common/LoadingSpinner";
import { TransactionStatus } from "../../model/Cart/TransactionStatus";
import { useQuery } from "../../utils/UseQuery";
import LifePaymentConfirmation from "./LifePaymentConfirmation";
import PaymentMethodSelectorDialog from "../payment/PaymentMethodSelectorDialog";
import { GetTranslation } from "../../translations/GetTranslation";
import { TranslationKey } from "../../translations/TranslationKey";
import LifePersonalDataDialog from "./LifePersonalDataDialog";
import { useGetMeQuery } from "../../api/rtk/MeApi";
import { CandidateViewModel } from "../../model/CandidateViewModel";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(2)}px`,
  },
  titleRow: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    paddingBottom: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: `${theme.spacing(2)}px`,
    alignItems: "center",
    backgroundColor: "#eee",
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(2)}px`,
  },
  title: {
    margin: 0,
    padding: 0,
  },
  payButton: {
    "& .MuiButton-label": {
      display: "flex",
      alignItems: "center",
      gap: `${theme.spacing(1)}px`,
    },
  },
}));

interface LifeExamDaysProps {
  session?: ExamSessionForSignupViewModel;
  sessionLanguage: Language;
}

const LifeExamDays: FC<LifeExamDaysProps> = ({ session, sessionLanguage }) => {
  const classes = useStyles();
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const languageKey = getKeyFromLanguage(sessionLanguage);
  const [busy, setBusy] = React.useState(false);
  const [me, setMe] = React.useState<CandidateViewModel | undefined>(undefined);
  const [selectedExamDay, setSelectedExamDay] = React.useState<SignupExamSessionExamDaysModel | undefined>(undefined);
  const deviceFingerprint = store.getState().sessionState.sessionId;
  const [selectedPaymentDetails, setSelectedPaymentDetails] = React.useState<ModuleSelectionExamDetails | undefined>(
    undefined
  );
  const [paidTransactionId, setPaidTransactionId] = React.useState<string | undefined>(undefined);
  const [showPersonalDataDialog, setShowPersonalDataDialog] = React.useState(false);
  const datatransTrxId = useQuery().get(UrlQueryParameters.datatransTrxId);

  const { data: fetchedMe, isFetching: isMeFetching } = useGetMeQuery();

  const course = session?.courses[0];
  const module = course?.modules[0];

  useEffect(() => {
    if (fetchedMe !== undefined) {
      setMe(() => ({
        ...fetchedMe,
        homeCountry: fetchedMe.nationalityIsSwiss ? Switzerland : fetchedMe.placeOfOrigin,
        employerCountry: fetchedMe.employerCountry || Switzerland,
        preferredContact: fetchedMe.privateIsPreferredContact ? "private" : fetchedMe.preferredContact,
      }));
    }
  }, [fetchedMe]);

  const {
    data: availableDays,
    isFetching,
    refetch,
  } = useGetLifeExamSessionDaysAvailableForSignUpQuery({
    language: sessionLanguage,
    examSessionId: session?.id ?? "",
  });

  if (isFetching || isMeFetching) {
    return <LoadingIndicator show={true} />;
  }

  if (!course) {
    return (
      <Alert severity="warning">
        <Typography>{intl.formatMessage({ id: "lifeExams.messages.courseNotFound" })}</Typography>
      </Alert>
    );
  }

  if (!module) {
    return <Alert severity="warning">{intl.formatMessage({ id: "lifeExams.messages.moduleNotFound" })}</Alert>;
  }

  const onPayButtonClick = (examDay: SignupExamSessionExamDaysModel) => {
    setSelectedExamDay(examDay);
    setShowPersonalDataDialog(true);
  };

  const handleSuccessPersonalData = () => {
    setShowPersonalDataDialog(false);
    handlePay(selectedExamDay!);
  };

  const handleCancelPersonalData = () => {
    setSelectedExamDay(undefined);
    setShowPersonalDataDialog(false);
  };

  const handlePay = (examDay: SignupExamSessionExamDaysModel) => {
    var exam = module.exams.find((exam) => exam.id === examDay.examId);
    if (!exam) {
      console.error("Exam not found");
      return;
    }

    const examDetails = {
      examId: examDay.examId,
      locationId: examDay.locationId,
      examDayId: examDay.examDayId,
      language: sessionLanguage,
      examType: exam.type,
    } as ModuleSelectionExamDetails;

    if (!examDay.isWaitingList && module.price > 0) {
      setSelectedPaymentDetails(examDetails);
    } else {
      // waiting list or free exam, proceed with booking
      startModulePayment(PaymentMethodType.CreditCard, examDetails);
    }
  };

  const onPaymentError = () => {
    setSelectedPaymentDetails(undefined);
  };

  const handlePayFromSelector = (paymentMethod: PaymentMethodType) => {
    startModulePayment(paymentMethod, selectedPaymentDetails!);
  };

  const handleModulePaid = () => {
    refetch();
  };

  const handleTransactionPaid = (transactionId: string) => {
    setPaidTransactionId(transactionId);
    refetch();
  };

  const startModulePayment = (paymentMethod: PaymentMethodType, examDetails: ModuleSelectionExamDetails) => {
    setBusy(true);
    initModulePayment({
      moduleId: module!.id,
      examSessionId: session!.id,
      courseId: course!.id,
      examDetails: [examDetails],
      paymentMethod,
      deviceFingerprint,
      isLifePayment: true,
      redirectUrl: window.location.href,
    })
      .then((response) => {
        if (response.success) {
          if (response.data.paymentUrl) {
            window.open(response.data.paymentUrl, "_self");
          } else if (response.data.status === TransactionStatus.Paid) {
            enqueueSnackbar(
              <TranslatedMessage
                id="startCertification.paymentAndCompletionStep.messages.transactionConfirmed"
                values={{ id: response.data.transactionId }}
              />,
              { variant: "success" }
            );

            handleModulePaid();
          }
        } else {
          onPaymentError();
        }
      })
      .catch(() => {
        onPaymentError();
      })
      .finally(() => setBusy(false));
  };

  if (datatransTrxId) {
    return (
      <LifePaymentConfirmation
        datatransTrxId={datatransTrxId}
        languageKey={languageKey}
        examSessionId={session.id}
        onPaidTransaction={handleTransactionPaid}
        paidTransactionId={paidTransactionId}
      />
    );
  }

  return (
    <>
      <Box className={classes.container}>
        <Box className={classes.titleRow}>
          <h3 className={classes.title}>
            <Typography>{GetTranslatedValueIntl(intl, session.sessionKey)}</Typography>
          </h3>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => navigateTo(routesPaths.life(languageKey), history)}
          >
            {intl.formatMessage({ id: "lifeExams.back" })}
          </Button>
        </Box>
        <Box>
          <Alert severity="info">
            <TranslatedMessage
              id={"lifeExams.messages.infoAlertExams"}
              values={{
                lang:
                  intl.locale !== Language.DE
                    ? GetTranslation(
                        intl,
                        `common.language.${sessionLanguage.toLocaleLowerCase()}` as TranslationKey
                      ).toLowerCase()
                    : GetTranslation(intl, `common.language.${sessionLanguage.toLocaleLowerCase()}` as TranslationKey),
              }}
            />
          </Alert>
        </Box>
        <Box>
          <Alert severity="warning">
            <strong>
              <TranslatedMessage id="startCertification.paymentAndCompletionStep.hint" />
            </strong>
          </Alert>
        </Box>
        <Box className={classes.list}>
          {availableDays &&
            availableDays.map((examDay) => (
              <Box key={examDay.examDayId} className={classes.row}>
                <Typography>
                  {examDay.isWaitingList ? (
                    <TranslatedMessage id="lifeExams.waitingList" />
                  ) : (
                    getDateFromString(examDay.date)
                  )}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  disabled={examDay.isFullyBooked || examDay.isBookedByCandidate}
                  onClick={() => onPayButtonClick(examDay)}
                  className={classes.payButton}
                >
                  <span>
                    <TranslatedMessage id={examDay.isFullyBooked ? "lifeExams.label.sesionFullyBooked" : examDay.isWaitingList ? "lifeExams.confirm" : "lifeExams.pay"} />
                  </span>
                  {!examDay.isFullyBooked && module.price > 0 && !examDay.isWaitingList &&  (
                    <span>{module.price.toLocaleString(SwissGermanLocale, { minimumFractionDigits: 2 })}</span>
                  )}
                </Button>
              </Box>
            ))}
        </Box>
        {selectedPaymentDetails && (
          <PaymentMethodSelectorDialog
            onClose={() => setSelectedPaymentDetails(undefined)}
            onPay={handlePayFromSelector}
          />
        )}
        {(busy || isMeFetching) && <LoadingSpinner />}
      </Box>
      <LifePersonalDataDialog
        me={me!}
        show={showPersonalDataDialog}
        handleSuccess={handleSuccessPersonalData}
        handleCancel={handleCancelPersonalData}
      />
    </>
  );
};

export default LifeExamDays;
